import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { EnglishFooterLinks } from "@/components/en/EnglishServicesLinks";
const logoImage = "/images/physiowrld-logo.webp";

const Footer = () => {
  const { t, i18n } = useTranslation();
  return (
    <footer className="bg-card border-t border-border/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src={logoImage} alt="PHYSIOWRLD klinika fizikalne terapije – logo" className="h-10 w-14" />
              <span className="text-2xl font-bold gradient-text">PHYSIOWRLD</span>
            </Link>
            <p className="text-muted-foreground text-sm">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/share/19X4S9QEjy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/physiowrld" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/physiowrld" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.services')}
                </Link>
              </li>
              <li>
                <Link to="/cenovnik" className="text-muted-foreground hover:text-foreground transition-colors">
                  {i18n.language === 'en' ? 'Price list' : i18n.language === 'ru' ? 'Прайс-лист' : 'Cenovnik'}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
              <li>
                <Link to="/booking" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.booking')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.services')}</h3>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li>
                <Link to="/kiropraktika-novi-beograd" className="hover:text-foreground transition-colors">
                  {t('services.kiropraktika.title')}
                </Link>
              </li>
              <li>
                <Link to="/services/sports-massage" className="hover:text-foreground transition-colors">
                  {t('services.sportsMassage.title')}
                </Link>
              </li>
              <li>
                <Link to="/services/laser" className="hover:text-foreground transition-colors">
                  {t('services.laser.title')}
                </Link>
              </li>
              <li>
                <Link to="/services/kinesitherapy" className="hover:text-foreground transition-colors">
                  {t('services.kinesitherapy.title')}
                </Link>
              </li>
              <li>
                <Link to="/fizikalna-terapija-novi-beograd" className="hover:text-foreground transition-colors">
                  {t('services.fizikalnaTerapija.title')}
                </Link>
              </li>
              <li>
                <Link to="/fizikalna-terapija-novi-beograd" className="hover:text-foreground transition-colors">
                  Fizikalna terapija Novi Beograd
                </Link>
              </li>
              <li>
                <Link to="/kiropraktika-novi-beograd" className="hover:text-foreground transition-colors">
                  Kiropraktika Novi Beograd
                </Link>
              </li>
              <li>
                <Link to="/shockwave-terapija-novi-beograd" className="hover:text-foreground transition-colors">
                  Shockwave terapija Novi Beograd
                </Link>
              </li>
              <li>
                <Link to="/presoterapija-novi-beograd" className="hover:text-foreground transition-colors">
                  Presoterapija Novi Beograd
                </Link>
              </li>
              <li>
                <Link to="/fizikalna-terapija-beograd" className="hover:text-foreground transition-colors">
                  Fizikalna terapija Beograd — kompletan vodič
                </Link>
              </li>
              <li>
                <Link to="/baza-znanja" className="hover:text-foreground transition-colors">
                  Baza znanja
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-foreground transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          <EnglishFooterLinks />

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
            <div className="space-y-3">
              <div className="space-y-2">
                <a href="tel:+381605037477" className="flex items-center space-x-3 text-sm hover:text-primary transition-colors">
                  <Phone className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">+381 60 503 7477</span>
                </a>
                <a href="https://wa.me/381605037477" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-sm text-emerald-400 hover:text-emerald-300 transition-colors ml-7">
                  <span className="text-sm">WhatsApp</span>
                </a>
              </div>
              <a href="mailto:physiowrld@gmail.com" className="flex items-center space-x-3 text-sm hover:text-primary transition-colors">
                <Mail className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">physiowrld@gmail.com</span>
              </a>
              <a href="https://maps.google.com/?q=Omladinskih+brigada+96E%2F4,+Novi+Beograd" target="_blank" rel="noopener noreferrer" className="flex items-start space-x-3 text-sm hover:text-primary transition-colors">
                <MapPin className="h-4 w-4 text-primary mt-0.5" />
                <span className="text-muted-foreground">
                  Omladinskih brigada 96E/4<br />
                  Novi Beograd, Beograd
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-border/20 mt-8 pt-8 text-center">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} PHYSIOWRLD. {t('footer.rights')} | {t('footer.description')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;